import detectrtc from 'detectrtc';
import { SbLoggerI } from './types';
import { VideoEffect, VideoEffectProcessor } from './webrtc/client';

export interface Device {
  deviceId: string;
  groupId: string;
  id: string;
  isCustomLabel?: boolean;
  kind: string;
  label: string;
}

export type MediaDevices = Device[];

const AUDIO_DEVICE_ID_KEY = '_sbAudioDeviceId_';
const AUDIO_OUTPUT_DEVICE_ID_KEY = '_sbAudioOutputDeviceId_';
const VIDEO_DEVICE_ID_KEY = '_sbVideoDeviceId_';

const ERROR_MSG = {
  NotAllowedError:
    'Could not get <device> media streams, make sure you have enabled device permissions in your browser',
  NotFoundError: 'Could not find <device>',
  NotReadableError: 'Failed to access <device> due to a hardware failure',
  OverconstrainedError: 'Could not get <device> that meets minimum requirements',
  TypeError:
    'Empty constraints or operating in an insecure context prevented <device> access',
  default:
    'Could not get <device> media streams, make sure you have devices available and enabled permissions in your browser',
};

export class SbMediaDevices {
  #log: SbLoggerI;
  #haveDevicesChanged = true;
  #hasEnumeratedAfterAllow = false;
  #deviceChangeTimeout: ReturnType<typeof setTimeout> | null = null;
  #streams: MediaStream[] = [];
  #videoEffectProcessor: null | VideoEffectProcessor = null;
  #unsubscribeDeviceChange: () => void;

  constructor(log: SbLoggerI) {
    this.#log = log;
    this.#unsubscribeDeviceChange = this.listenToDeviceChange(
      () => (this.#haveDevicesChanged = true),
    );
  }

  /**
   * Call to end all streams and cleanup all listeners.
   */
  destroy() {
    this.#streams.forEach(stream => {
      stream.getTracks().forEach(t => t.stop());
    });
    this.#unsubscribeDeviceChange();
  }

  /**
   * Returns a list of audio and video unput devices. Information may be obscured if the
   * user has not enabled permissions.
   */
  getMediaDevices = async (): Promise<{
    audioInputDevices: Device[];
    videoInputDevices: Device[];
    audioOutputDevices: Device[];
  }> => {
    await this.#refreshDetectRtc();

    const { audioOutputDevices, audioInputDevices, videoInputDevices } = detectrtc;
    return {
      audioInputDevices: audioInputDevices as Device[],
      videoInputDevices: videoInputDevices as Device[],
      audioOutputDevices: audioOutputDevices as Device[],
    };
  };

  /**
   * Returns the user's currently configured preferred devices. If the user has
   * not selected devices before, returns the first available device. If the user
   * has no configuration or devices, returns undefined.
   */
  getPreferredMediaDevices = async () => {
    // For some reason when this is called from setupUserMediaStream the
    // input device arrays on detectrtc get reset, and we need to "force"
    // refreshDetectRtc (usually it will check a dirty flag). Without
    // the force we never get our preferred devices from this fn.
    await this.#refreshDetectRtc(true);
    let audio =
      localStorage.getItem(AUDIO_DEVICE_ID_KEY) ||
      detectrtc.audioInputDevices[0]?.deviceId ||
      undefined;
    let audioOutput =
      localStorage.getItem(AUDIO_OUTPUT_DEVICE_ID_KEY) ||
      detectrtc.audioOutputDevices[0]?.deviceId ||
      undefined;
    let video =
      localStorage.getItem(VIDEO_DEVICE_ID_KEY) ||
      detectrtc.videoInputDevices[0]?.deviceId ||
      undefined;

    if (audio && !detectrtc.audioInputDevices.map(x => x.deviceId).includes(audio)) {
      this.#log.info(`Preferred mic device ${audio} not found, will use first available`);
      audio = detectrtc.audioInputDevices[0]?.deviceId || undefined;
    }
    if (
      audioOutput &&
      !detectrtc.audioOutputDevices.map(x => x.deviceId).includes(audioOutput)
    ) {
      this.#log.info(
        `Preferred speaker device ${audioOutput} not found, will use first available`,
      );
      audioOutput = detectrtc.audioOutputDevices[0]?.deviceId || undefined;
    }
    if (video && !detectrtc.videoInputDevices.map(x => x.deviceId).includes(video)) {
      this.#log.info(`Preferred cam device ${video} not found, will use first available`);
      video = detectrtc.videoInputDevices[0]?.deviceId || undefined;
    }
    return { audio, video, audioOutput };
  };

  /**
   * Updates the users' preferred media devices. If passed a falsy value, does not
   * override the existing saved preference.
   */
  setPreferredMediaDevices = (
    audioDeviceId?: string,
    videoDeviceId?: string,
    audioOutputDeviceId?: string,
  ) => {
    // Save preferred devices in LS
    if (audioDeviceId) {
      localStorage.setItem(AUDIO_DEVICE_ID_KEY, audioDeviceId);
    }
    if (videoDeviceId) {
      localStorage.setItem(VIDEO_DEVICE_ID_KEY, videoDeviceId);
    }
    if (audioOutputDeviceId) {
      localStorage.setItem(AUDIO_OUTPUT_DEVICE_ID_KEY, audioOutputDeviceId);
    }
  };

  /**
   * Trigger callback when devices change. Returns an unsubscribe function.
   */
  listenToDeviceChange(onDeviceChange: () => void): () => void {
    if (!navigator?.mediaDevices) return () => null;
    const listener = () => {
      if (this.#deviceChangeTimeout) clearTimeout(this.#deviceChangeTimeout);
      this.#deviceChangeTimeout = setTimeout(() => {
        onDeviceChange();
        this.#deviceChangeTimeout = null;
      }, 100);
    };
    navigator.mediaDevices.addEventListener('devicechange', listener);
    return () => navigator.mediaDevices.removeEventListener('devicechange', listener);
  }

  /**
   * Starts a user media stream. Pass boolean or string for audio and video to ask for them
   * individually. Passing true will default to using preferred media device, string will
   * request a specific media device.
   */
  async setupCameraMediaStream({
    audio,
    video,
    audioOutput,
    minWidth,
    minHeight,
    optimalWidth,
    optimalHeight,
    maxWidth,
    maxHeight,
    maxFrameRate,
  }: {
    audio?: string | boolean;
    video?: string | boolean;
    audioOutput?: string | boolean;
    minWidth?: number;
    minHeight?: number;
    optimalWidth?: number;
    optimalHeight?: number;
    maxWidth?: number;
    maxHeight?: number;
    maxFrameRate?: number;
  }) {
    const { mediaDevices } = navigator;
    if (!mediaDevices) {
      this.#log.warn('Could not access navigator.mediaDevices');
      throw new Error('Your browser does not support camera streaming');
    }

    let audioDeviceId: string | undefined;
    let audioOutputDeviceId: string | undefined;
    let videoDeviceId: string | undefined;

    try {
      // Assemble constraints
      const preferred = await this.getPreferredMediaDevices();
      const constraints: MediaStreamConstraints & {
        audioOutput?: boolean | MediaTrackConstraints;
      } = {
        audio: false,
        video: false,
        audioOutput: false,
      };
      if (audio) {
        audioDeviceId = typeof audio === 'string' ? audio : preferred.audio;
        constraints.audio =
          typeof audioDeviceId === 'string' ? { deviceId: audioDeviceId } : true;
      }

      if (audioOutput) {
        audioOutputDeviceId =
          typeof audioOutput === 'string' ? audioOutput : preferred.audioOutput;
        constraints.audioOutput =
          typeof audioOutputDeviceId === 'string'
            ? { deviceId: audioOutputDeviceId }
            : true;
      }

      if (video) {
        videoDeviceId = typeof video === 'string' ? video : preferred.video;

        const width: Partial<MediaTrackConstraints['width']> = {};
        if (minWidth !== undefined) width.min = minWidth;
        if (maxWidth !== undefined) width.max = maxWidth;
        if (optimalWidth !== undefined) width.ideal = optimalWidth;

        const height: Partial<MediaTrackConstraints['height']> = {};
        if (minHeight !== undefined) height.min = minHeight;
        if (maxHeight !== undefined) height.max = maxHeight;
        if (optimalHeight !== undefined) height.ideal = optimalHeight;

        const videoConstraints: MediaTrackConstraints = {
          facingMode: 'user',
          ...(videoDeviceId && { deviceId: videoDeviceId }),
          ...(Object.keys(width).length > 0 && { width }),
          ...(Object.keys(height).length > 0 && { height }),
          ...(maxFrameRate !== undefined && { frameRate: { max: maxFrameRate } }),
        };
        constraints.video = videoConstraints;
      }

      // Get stream, update preferences if successful
      const stream = this.#applyVideoEffects(
        await mediaDevices.getUserMedia(constraints),
      );

      this.setPreferredMediaDevices(
        typeof constraints.audio === 'object'
          ? (constraints.audio.deviceId as string)
          : undefined,
        typeof constraints.video === 'object'
          ? (constraints.video.deviceId as string)
          : undefined,
        typeof constraints.audioOutput === 'object'
          ? (constraints.audioOutput.deviceId as string)
          : undefined,
      );
      await this.#triggerDeviceChangeAfterPermissions();

      const stopVideo = () => {
        stream.getVideoTracks().forEach(t => t.stop());
        this.#videoEffectProcessor?.stop(stream);
      };

      const stopAudio = () => {
        stream.getAudioTracks().forEach(t => t.stop());
      };

      const stopAll = () => {
        stopVideo();
        stopAudio();
      };

      return {
        stream,
        audioDeviceId,
        videoDeviceId,
        audioOutputDeviceId,
        stopVideo,
        stopAudio,
        stopAll,
      };
    } catch (error) {
      const errorName = ((error as Error).name || 'default') as keyof typeof ERROR_MSG;
      const device =
        audio && video ? 'microphone and camera' : audio ? 'microphone' : 'camera';
      const msg = (ERROR_MSG[errorName] || ERROR_MSG.default).replace('<device>', device);
      this.#log.warn(msg, { error, audioDeviceId, videoDeviceId });
      const bubbledErr = new Error(msg);
      bubbledErr.name = errorName || 'GetUserMediaFailed';
      throw bubbledErr;
    }
  }

  /**
   * Starts a display media stream. Most browsers will always pop open a prompt, so may
   * take some time for the promise to resolve.
   */
  async setupDisplayMediaStream(): Promise<MediaStream> {
    if (!navigator?.mediaDevices?.getDisplayMedia) {
      throw new Error('Your browser does not support screen capture');
    }
    try {
      return await navigator.mediaDevices.getDisplayMedia({
        audio: true,
        video: {
          cursor: 'always',
          displaySurface: 'browser',
        },
      } as any);
    } catch (error) {
      this.#log.warn('Failed to getDisplayMedia', { error });
      throw error;
    }
  }

  isVideoProcessingSupported() {
    return !!this.#videoEffectProcessor;
  }

  setVideoEffectProcessor(processor: null | VideoEffectProcessor) {
    this.#videoEffectProcessor = processor;
  }

  async setVideoEffect(effect: null | VideoEffect, backgroundImageId?: string) {
    this.#log.info(`Setting video effect to ${effect}.`);
    await this.#videoEffectProcessor?.prepare(effect, backgroundImageId);
  }

  #applyVideoEffects(stream: MediaStream): MediaStream {
    if (this.#videoEffectProcessor) {
      return this.#videoEffectProcessor.apply(stream);
    }
    return stream;
  }

  /**
   * Firefox doesn't show labels for devices until permissions are granted, so refresh
   * detectrtc when we know we have them.
   */
  #triggerDeviceChangeAfterPermissions() {
    return new Promise<void>(res => {
      detectrtc.load(() => {
        if (
          !this.#hasEnumeratedAfterAllow &&
          detectrtc.isWebsiteHasMicrophonePermissions
        ) {
          this.#log.debug('Refreshing device list after permissions granted');
          navigator.mediaDevices.dispatchEvent(new Event('devicechange'));
          this.#hasEnumeratedAfterAllow = true;
        }
        res();
      });
    });
  }

  /**
   * Refresh detectrtc if their devices have changed (e.g. pluggin mic in) or by force.
   */
  #refreshDetectRtc(force = false) {
    return new Promise<void>(res => {
      if (this.#haveDevicesChanged || force) {
        detectrtc.load(res);
      } else {
        res();
      }
    }).then(() => {
      this.#haveDevicesChanged = false;
    });
  }
}
